import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Loader2, Check, X, ShieldCheck, Inbox, Undo2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (s) => { try { return new Date(s).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }); } catch { return s; } };

const FieldRow = ({ pid, entry, onDecided }) => {
  const [verified, setVerified] = useState(true);
  const [busy, setBusy] = useState(false);
  const decide = async (action) => {
    setBusy(true);
    try {
      await axios.post(`${API}/listing-proposals/${pid}/field`, { field: entry.field, action, verified });
      toast.success(action === 'accept' ? `${entry.label} accepted` : `${entry.label} rejected`);
      onDecided();
    } catch { toast.error('Action failed'); } finally { setBusy(false); }
  };
  const undo = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/listing-proposals/${pid}/field/undo`, { field: entry.field });
      toast.success(`${entry.label} decision undone - back to pending`);
      onDecided();
    } catch { toast.error('Undo failed'); } finally { setBusy(false); }
  };
  const decided = entry.status !== 'pending';
  return (
    <div className="border border-[#E5E7EB] rounded-lg p-4" data-testid={`proposal-field-${pid}-${entry.field}`}>
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold text-gray-900">{entry.label}</p>
        {decided && (
          <div className="flex items-center gap-2">
            <Badge className={entry.status === 'accepted' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>{entry.status}{entry.status === 'accepted' && entry.verified ? ' · verified' : ''}</Badge>
            <Button size="sm" variant="ghost" onClick={undo} disabled={busy} className="h-7 px-2 text-xs text-gray-500 hover:text-gray-900" data-testid={`proposal-undo-${pid}-${entry.field}`}><Undo2 className="w-3.5 h-3.5 mr-1" /> Undo</Button>
          </div>
        )}
      </div>
      <div className="grid sm:grid-cols-2 gap-3 mt-2">
        <div className="bg-gray-50 rounded p-2.5"><p className="text-[11px] uppercase text-gray-400 font-semibold mb-1">Current</p><p className="text-sm text-gray-600 break-words">{String(entry.current_value ?? '-')}</p></div>
        <div className="bg-[#00A79D]/5 rounded p-2.5 border border-[#00A79D]/20"><p className="text-[11px] uppercase text-[#00857D] font-semibold mb-1">Proposed</p><p className="text-sm text-gray-800 break-words">{String(entry.proposed_value)}</p></div>
      </div>
      {!decided && (
        <div className="flex items-center gap-3 mt-3">
          <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer" data-testid={`proposal-verified-${pid}-${entry.field}`}>
            <input type="checkbox" checked={verified} onChange={(e) => setVerified(e.target.checked)} className="w-4 h-4 accent-[#00A79D]" /> Mark verified
          </label>
          <Button size="sm" onClick={() => decide('accept')} disabled={busy} className="h-8 bg-[#00A79D] hover:bg-[#00857D]" data-testid={`proposal-accept-${pid}-${entry.field}`}><Check className="w-4 h-4 mr-1" /> Accept</Button>
          <Button size="sm" variant="outline" onClick={() => decide('reject')} disabled={busy} className="h-8 text-red-600 border-red-200 hover:bg-red-50" data-testid={`proposal-reject-${pid}-${entry.field}`}><X className="w-4 h-4 mr-1" /> Reject</Button>
        </div>
      )}
    </div>
  );
};

const ListingProposals = () => {
  const [data, setData] = useState({ items: [], pending_count: 0 });
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('pending');

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await axios.get(`${API}/listing-proposals`, { params: { status: tab } }); setData(r.data); }
    finally { setLoading(false); }
  }, [tab]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6" data-testid="listing-proposals-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Listing update proposals</h1>
        <p className="text-gray-500 text-sm mt-1">Suggested edits from suppliers and the public. Accept or reject each change field by field; accepted facts are stamped Claimed or Verified.</p>
      </div>

      <div className="flex gap-2">
        {['pending', 'resolved', 'all'].map((t) => (
          <button key={t} onClick={() => setTab(t)} className={`text-sm font-medium px-3 py-1.5 rounded-md capitalize ${tab === t ? 'bg-[#00A79D] text-white' : 'text-gray-600 hover:bg-gray-100'}`} data-testid={`proposals-tab-${t}`}>{t}</button>
        ))}
      </div>

      {loading ? <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div> : (
        <div className="space-y-4">
          {data.items.length === 0 && <div className="text-center py-16 text-gray-400 border border-dashed border-gray-200 rounded-lg"><Inbox className="w-8 h-8 mx-auto mb-2" />No {tab} proposals.</div>}
          {data.items.map((p) => (
            <Card key={p.id} data-testid={`proposal-${p.id}`}><CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="font-semibold text-gray-900">{p.company_name}</p>
                  <p className="text-xs text-gray-400">From {p.submitted_by_name || p.submitted_by_email} · {p.source} · {fmt(p.created_at)}</p>
                </div>
                <Badge className={p.status === 'pending' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'}>{p.status}</Badge>
              </div>
              <div className="space-y-3">
                {p.fields.map((f) => <FieldRow key={f.field} pid={p.id} entry={f} onDecided={load} />)}
              </div>
            </CardContent></Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default ListingProposals;
